import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore } from './store/authStore';
import { AdminLayout } from './components/admin';
import JoinRedirect from './pages/JoinRedirect';

// Download redirect — detects platform and redirects to appropriate store
const DownloadRedirect = () => {
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    
    if (isAndroid) {
      window.location.href = 'https://nepro.onelink.me/IlCs?pid=organic&c=App_Root_Redirect';
    } else if (isIOS) {
      // TODO: Replace with App Store link when available
      window.location.href = 'https://nepro.onelink.me/IlCs?pid=organic&c=App_Root_Redirect';
    } else {
      // Desktop — show Play Store link
      window.location.href = 'https://nepro.onelink.me/IlCs?pid=organic&c=App_Root_Redirect';
    }
  }, []);
  
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial, sans-serif', flexDirection: 'column', gap: '16px' }}>
      <div style={{ fontSize: '48px' }}>📱</div>
      <h2 style={{ color: '#7210FF' }}>Redirecting to NePro App...</h2>
      <p>If not redirected, <a href="https://nepro.onelink.me/IlCs?pid=organic&c=App_Root_Redirect" style={{ color: '#7210FF' }}>click here</a></p>
    </div>
  );
};

// Lazy load pages for code splitting
const Landing = lazy(() => import('./pages/Landing'));
const Talk = lazy(() => import('./pages/Talk'));
const Login = lazy(() => import('./pages/Login'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const DataDeletion = lazy(() => import('./pages/DataDeletion'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminProviders = lazy(() => import('./pages/admin/Providers'));
const AdminCustomers = lazy(() => import('./pages/admin/Customers'));
const AdminListings = lazy(() => import('./pages/admin/Listings'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminMap = lazy(() => import('./pages/admin/Map'));
const AdminConfig = lazy(() => import('./pages/admin/Config'));
const AdminNotifications = lazy(() => import('./pages/admin/Notifications'));
const AdminReviews = lazy(() => import('./pages/admin/Reviews'));
const AdminNotificationsCenter = lazy(() => import('./pages/admin/NotificationsCenter'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminAIOperations = lazy(() => import('./pages/admin/AIOperations'));
const AdminLeads = lazy(() => import('./pages/admin/Leads'));
const AdminMetaAdsLanding = lazy(() => import('./pages/admin/MetaAdsLanding'));
const AdminEngagement = lazy(() => import('./pages/admin/Engagement'));
const AdminAppsFlyerAnalytics = lazy(() => import('./pages/admin/AppsFlyerAnalytics'));
const AdminMessageOrchestrator = lazy(() => import('./pages/admin/MessageOrchestrator'));
const AdminNotificationOverview = lazy(() => import('./pages/admin/NotificationOverview'));
const AdminEmailPreviews = lazy(() => import('./pages/admin/EmailPreviews'));
const SPRedirect = lazy(() => import('./pages/SPRedirect'));
const ListingRedirect = lazy(() => import('./pages/ListingRedirect'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary-200 border-t-primary rounded-full animate-spin" />
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Page loading component (smaller, for within admin layout)
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-3 border-primary-200 border-t-primary rounded-full animate-spin" />
  </div>
);

// Auth initializer - sets up Firebase auth listener
const AuthInitializer = ({ children }: { children: React.ReactNode }) => {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    const unsubscribe = initialize();
    return () => unsubscribe();
  }, [initialize]);

  return <>{children}</>;
};

// Protected route wrapper for admin routes
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAdmin, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthInitializer>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/talk" element={<Talk />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/download" element={<DownloadRedirect />} />
          <Route path="/join" element={<JoinRedirect />} />
          {/* Deep link redirects — opens app or shows download page */}
          <Route path="/sp/:spId" element={<SPRedirect />} />
          <Route path="/s/:listingId" element={<ListingRedirect />} />
          <Route path="/listing/:listingId" element={<ListingRedirect />} />

          {/* Admin Routes (Protected with shared layout) */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              }
            />
            <Route
              path="dashboard"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              }
            />
            <Route
              path="providers"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminProviders />
                </Suspense>
              }
            />
            <Route
              path="customers"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminCustomers />
                </Suspense>
              }
            />
            <Route
              path="listings"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminListings />
                </Suspense>
              }
            />
            <Route
              path="orders"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminOrders />
                </Suspense>
              }
            />
            <Route
              path="map"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminMap />
                </Suspense>
              }
            />
            <Route
              path="categories"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminCategories />
                </Suspense>
              }
            />
            <Route
              path="config"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminConfig />
                </Suspense>
              }
            />
            <Route
              path="notifications"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminNotifications />
                </Suspense>
              }
            />
            <Route
              path="notifications-center"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminNotificationsCenter />
                </Suspense>
              }
            />
            <Route
              path="ai-ops"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminAIOperations />
                </Suspense>
              }
            />
            <Route
              path="reviews"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminReviews />
                </Suspense>
              }
            />
            <Route
              path="leads"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminLeads />
                </Suspense>
              }
            />
            <Route
              path="meta-ads-landing"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminMetaAdsLanding />
                </Suspense>
              }
            />
            <Route
              path="appsflyer"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminAppsFlyerAnalytics />
                </Suspense>
              }
            />
            <Route
              path="engagement"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminEngagement />
                </Suspense>
              }
            />
            <Route
              path="message-orchestrator"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminMessageOrchestrator />
                </Suspense>
              }
            />
            <Route
              path="notification-overview"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminNotificationOverview />
                </Suspense>
              }
            />
            <Route
              path="email-previews"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminEmailPreviews />
                </Suspense>
              }
            />
          </Route>

            {/* 404 Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthInitializer>
    </BrowserRouter>
  );
}

export default App;
